import { createRouter, createWebHistory } from 'vue-router'
import { useLoadingStore } from '@/stores/loading'
import { useSidebarStore } from '@/stores/sidebar'
import { getAuth, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth'
import { getUserData } from '@/services/user.service'
import StudentLayout from '@/views/layout/StudentLayout.vue'
import PeopleService from '@/services/people.service'
import Level from '@/enums/level.enum'
import useAclStore from '@/stores/acl'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/auth/login',
      name: 'login',
      component: () => import('../views/auth/LoginView.vue')
    },
    {
      path: '/auth/register',
      name: 'register',
      component: () => import('../views/auth/RegisterView.vue')
    },
    {
      path: '/auth/complete-registration',
      name: 'complete-registration',
      component: () => import('../views/auth/CompleteRegistrationView.vue')
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: () => import('../views/organization/OrganizationListView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/',
      component: () => import('../views/layout/MainLayout.vue'),
      redirect: '/organization-info',
      meta: { requiresAuth: true },
      children: [
        {
          path: '/applications/:id/edit',
          name: 'application-edit',
          component: () => import('../views/application/ApplicationEditView.vue')
        },
        {
          path: '/applications/:id/view',
          name: 'application-view',
          component: () => import('../views/application/ApplicationView.vue')
        },
        {
          path: '/exams',
          name: 'exams',
          component: () => import('../views/exam/ExamListView.vue')
        },
        {
          path: '/exams/new',
          name: 'newExam',
          component: () => import('../views/exam/NewExamView.vue')
        },
        {
          path: '/exams/:id/edit',
          name: 'exam',
          component: () => import('../views/exam/ExamView.vue')
        },
        {
          path: '/exams/:id/edit-model',
          name: 'exam-model',
          component: () => import('../views/exam/ExamViewModel.vue')
        },
        {
          path: '/exams/:id/details',
          name: 'exam-details',
          component: () => import('../views/exam/ExamDetailView.vue')
        },
        {
          path: '/exams/:id/application',
          name: 'exam-application',
          component: () => import('../views/application/ApplicationCreateView.vue')
        },
        {
          path: '/questions',
          name: 'questions',
          component: () => import('../views/question/QuestionListView.vue')
        },
        {
          path: '/questions/:id/edit',
          name: 'editQuestion',
          component: () => import('../views/question/EditQuestionView.vue')
        },
        {
          path: '/new-question',
          name: 'new-question',
          component: () => import('../views/question/CreateQuestionView.vue')
        },
        {
          path: '/organization-profile',
          name: 'Perfil',
          component: () => import('../views/organization/OrganizationProfileView.vue')
        },
        {
          path: '/organization-info',
          name: 'organizationInfo',
          component: () => import('../views/organization/OrganizationInfoView.vue'),
          meta: { requiresTeacher: true }
        },
        {
          path: '/school-info/:id',
          name: 'school',
          component: () => import('../views/school/SchoolInfoView.vue')
        },
        {
          path: '/school-info/:schoolId/course-info/:courseId',
          name: 'course',
          component: () => import('../views/course/CourseInfoView.vue')
        },
        {
          path: '/school-info/:schoolId/teacher-info/:teacherUserId',
          name: 'teacher',
          component: () => import('../views/teacher/TeacherInfoView.vue')
        },
        {
          path: '/school-info/:schoolId/course-info/:courseId/student-info/:studentUserId',
          name: 'student',
          component: () => import('../views/student/StudentInfoView.vue')
        },
        {
          path: '/schools-student-login',
          name: 'schoolsStudentLogin',
          component: () => import('../views/student-login/SchoolsStudentsLogin.vue')
        },
        {
          path: '/schools-student-login/:id/courses-student-login',
          name: 'coursesStudentLogin',
          component: () => import('../views/student-login/CoursesStudentsLogin.vue')
        },
        {
          path: '/schools-student-login/:schoolId/courses-student-login/:courseId/students',
          name: 'studentsLogin',
          component: () => import('../views/student-login/StudentsLogin.vue')
        },
        {
          path: '/support-material',
          name: 'support-material',
          component: () => import('../views/support-material/ListMaterial.vue')
        },
        {
          path: '/class-plans',
          name: 'class-plans',
          component: () => import('@/views/class-plans/ClassPlansList.vue')
        }
      ]
    },
    {
      path: '/student',
      component: StudentLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'open-exams',
          name: 'student-open-exams',
          component: () => import('../views/student/StudentOpenExamsView.vue')
        },
        {
          path: 'solve-exam/:examId',
          name: 'student-solve-exam',
          component: () => import('../views/student/StudentSolveExamView.vue')
        },
        {
          path: 'results/:examId',
          name: 'student-results',
          component: () => import('../views/student/StudentResultsView.vue')
        },
        {
          path: 'completed-exams',
          name: 'student-completed-exams',
          component: () => import('../views/student/StudentCompletedExamsView.vue')
        },
        {
          path: 'profile',
          name: 'student-profile',
          component: () => import('../views/organization/OrganizationProfileView.vue'),
          meta: { requiresStudent: true }
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
})

// Função auxiliar para verificar se o usuário é estudante
const isStudent = (levels: any[]): boolean => {
  return levels.some((l: any) => l.level === Level.STUDENT)
}

// Função auxiliar para verificar se o usuário tem papel organizacional
const isOrganizationalRole = (levels: any[]): boolean => {
  return levels.some((l: any) => Number(l.level) >= Number(Level.TEACHER))
}

router.beforeEach(async (to) => {
  useLoadingStore().startLoading()

  const auth = getAuth()
  const user = await new Promise((resolve) => {
    onAuthStateChanged(auth, resolve)
  })

  if (to?.query?.customToken) {
    await signInWithCustomToken(getAuth(), to.query.customToken as string)

    const userDataResult = await getUserData()
    const userMaxLevel = await new PeopleService().getUserMaxLevel()

    if (!userDataResult?.data()?.active_organization?.id) {
      await router.push({ name: 'organizations' })
      return
    }

    if (userDataResult?.data()?.is_super_admin) {
      if (userDataResult.data()?.active_organization?.id) {
        localStorage.setItem('activeOrganization', userDataResult.data().active_organization.id)
      }
      await router.push('/organization-info')
      return
    }

    if (parseInt(userMaxLevel) >= parseInt(Level.TEACHER)) {
      await router.push('/organization-profile')
      return
    }

    await router.push('/student/open-exams')
  }

  // Verificar autenticação
  if (!user && to.meta?.requiresAuth) {
    return router.push('/auth/login')
  }

  // Verificar permissões específicas
  if (user) {
    const userDoc = await getUserData()
    const userData = userDoc?.data()

    if (userData?.id) {
      const aclStore = useAclStore()

      // Verificar se é estudante para rotas que requerem estudante
      if (to.meta?.requiresStudent && aclStore.userPermissions.length === 0) {
        const userService = new (await import('@/services/user.service')).default()
        const levels = await userService.getUserLevels()
        const isStudentUser = isStudent(levels)

        if (!isStudentUser) {
          return router.push('/auth/login')
        }
      }

      // Verificar se é professor para rotas que requerem professor
      if (to.meta?.requiresTeacher) {
        const userMaxLevel = await new PeopleService().getUserMaxLevel()
        if (parseInt(userMaxLevel) < parseInt(Level.TEACHER)) {
          return router.push('/student/open-exams')
        }
      }

      // Verificar se é estudante para rotas que requerem estudante
      if (to.meta?.requiresStudent) {
        const userMaxLevel = await new PeopleService().getUserMaxLevel()
        if (parseInt(userMaxLevel) >= parseInt(Level.TEACHER)) {
          return router.push('/organization-info')
        }
      }
    }
  }
})

router.afterEach((to) => {
  window.scrollTo(0, 0)
  useLoadingStore().stopLoading()

  if (to?.query?.sidebar?.length) {
    useSidebarStore().showCollapsable(to.query.sidebar as string)
  }
})

export default router
